import React from 'react';
import PropTypes from 'prop-types';
import { v1 as uuid } from 'uuid';
import { useHistory } from 'react-router-dom';
import KebabMenu from '../KebabMenu';
import Title from './Title';
import {
  StyledContent,
  StyledFooter,
  StyledFooterWrapper,
  StyledKebabMenuWrapper,
  StyledActiveListTimer,
  StyledContentContainer,
  StyledThumbnail,
  StyledTradeCard,
  StyledTitle
} from './Styles';

const propTypes = {
  id: PropTypes.string,
  testId: PropTypes.string,
  variant: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  thumbnailWidth: PropTypes.number,
  thumbnailHeight: PropTypes.number,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.number
  ]),
  trade: PropTypes.shape({
    make: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    model: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.number
  ]),
  footer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.number
  ]),
  activeTimer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.number
  ]),
  onClick: PropTypes.func,
  redirectLink: PropTypes.string,
  handleNavigateToTradeUri: PropTypes.func,
  bidStatus: PropTypes.number,
  isInSaleEvent: PropTypes.bool,
  isLastTradeInSaleEvent: PropTypes.bool,
  cardFooters: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.number])
  ),
  kebabPosition: PropTypes.oneOf(['top', 'bottom']),
  kebabOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
        PropTypes.number
      ]).isRequired,
      onClick: PropTypes.func.isRequired,
      isCopy: PropTypes.bool,
      disabled: PropTypes.bool
    })
  ),
  isTimerExtended: PropTypes.bool,
  isOmp: PropTypes.bool
};

const defaultProps = {
  id: 'list-card',
  testId: 'list-card',
  variant: 'list',
  thumbnailUrl: null,
  thumbnailWidth: null,
  thumbnailHeight: null,
  title: null,
  trade: undefined,
  content: null,
  footer: null,
  activeTimer: null,
  onClick: undefined,
  redirectLink: undefined,
  handleNavigateToTradeUri: undefined,
  bidStatus: undefined,
  isInSaleEvent: false,
  isLastTradeInSaleEvent: false,
  cardFooters: [],
  kebabPosition: 'top',
  kebabOptions: [],
  isTimerExtended: false,
  isOmp: false
};

const ListCard = ({
  id,
  testId,
  variant,
  thumbnailUrl,
  thumbnailWidth,
  thumbnailHeight,
  title,
  trade,
  content,
  footer,
  activeTimer,
  onClick,
  redirectLink,
  handleNavigateToTradeUri,
  bidStatus,
  isInSaleEvent,
  isLastTradeInSaleEvent,
  cardFooters,
  kebabOptions,
  kebabPosition,
  isTimerExtended,
  isOmp
}) => {
  const history = useHistory();

  const handleClick = event => {
    event.stopPropagation();
    event.preventDefault();
    event.persist();

    if (onClick) {
      onClick(event);
    }
    if (redirectLink) {
      if (event?.ctrlKey || event?.metaKey) {
        window.open(redirectLink, '_blank', 'noreferrer');
      } else if (handleNavigateToTradeUri) {
        handleNavigateToTradeUri(redirectLink);
      } else {
        history.push(redirectLink);
      }
    }
  };

  return (
    <StyledTradeCard
      id={id}
      data-testid={testId}
      variant={variant}
      isInSaleEvent={isInSaleEvent}
      isLastTradeInSaleEvent={isLastTradeInSaleEvent}
      isOmp={isOmp}
    >
      <StyledThumbnail
        data-testid={`${testId}-thumbnail`}
        url={thumbnailUrl}
        width={thumbnailWidth}
        height={thumbnailHeight}
        variant={variant}
        onClick={handleClick}
        hasActions={redirectLink || onClick}
        hasFooters={(cardFooters?.length > 0).toString()}
        href={redirectLink}
      >
        {activeTimer && (
          <StyledActiveListTimer
            data-testid={`${testId}-timer`}
            isTimerExtended={isTimerExtended}
          >
            {activeTimer}
          </StyledActiveListTimer>
        )}
      </StyledThumbnail>
      <StyledContentContainer
        testId={`${testId}-body`}
        bidStatus={bidStatus}
        variant={variant}
        isInSaleEvent={isInSaleEvent}
        height={thumbnailHeight}
      >
        {(title || trade) && (
          <StyledTitle
            onClick={handleClick}
            href={redirectLink}
            data-testid={`${testId}-title`}
            hasActions={redirectLink || onClick}
          >
            {trade ? <Title trade={trade} /> : title}
          </StyledTitle>
        )}
        {content && (
          <StyledContent testId={`${testId}-content`}>{content}</StyledContent>
        )}
        {footer && (
          <StyledFooter testId={`${testId}-body-footer`}>{footer}</StyledFooter>
        )}
        {kebabOptions.length > 0 && (
          <StyledKebabMenuWrapper
            position={kebabPosition}
            data-testid={`${testId}-kebab-menu-wrapper`}
          >
            <KebabMenu testId={`${testId}-kebab-menu`} actions={kebabOptions} />
          </StyledKebabMenuWrapper>
        )}
      </StyledContentContainer>
      {cardFooters?.length > 0 && (
        <StyledFooterWrapper data-testid={`${testId}-footers`}>
          {cardFooters.map(FooterComponent => (
            <div data-testid={`${testId}-footer`} key={`footer-${uuid()}`}>
              {FooterComponent}
            </div>
          ))}
        </StyledFooterWrapper>
      )}
    </StyledTradeCard>
  );
};

ListCard.propTypes = propTypes;
ListCard.defaultProps = defaultProps;

export default ListCard;
